package dev.lingvo.site.ds

import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.await
import org.w3c.dom.HTMLTextAreaElement

/**
 * Put [value] on the clipboard, or throw.
 *
 * **Every caller is showing something that cannot be shown twice** — an API token, an agent token, a
 * rotated secret. The async clipboard API is unavailable on insecure origins and refused outright by
 * some browsers when the click has already gone through an `await`, so the hidden-textarea path is
 * not legacy politeness: without it the copy button silently does nothing on exactly the screen
 * where doing nothing costs somebody their credential.
 *
 * Throws rather than returning false, so a caller cannot accidentally report success. There is no
 * safe default here — "copied" when nothing was copied is the failure this whole function exists to
 * prevent.
 */
suspend fun copySafely(value: String) {
    copyWithFallback(
        value = value,
        primary = { candidate -> window.navigator.clipboard.writeText(candidate).await() },
        fallback = ::copyWithTextarea,
    )
}

/** Runs the synchronous browser fallback only when the primary clipboard genuinely failed. */
internal suspend fun copyWithFallback(
    value: String,
    primary: suspend (String) -> Unit,
    fallback: (String) -> Unit,
) {
    try {
        primary(value)
    } catch (cancelled: CancellationException) {
        // Cancellation means the owning screen/action retired. Falling back here would turn that
        // retirement into a successful copy, allowing one-time-secret flows to publish or navigate.
        throw cancelled
    } catch (_: Throwable) {
        fallback(value)
    }
}

private fun copyWithTextarea(value: String) {
    val body = document.body ?: error("Clipboard is unavailable.")
    val area = document.createElement("textarea") as HTMLTextAreaElement
    area.value = value
    area.setAttribute("readonly", "")
    area.style.setProperty("position", "fixed")
    area.style.setProperty("left", "-9999px")
    body.appendChild(area)
    try {
        area.select()
        if (!document.execCommand("copy")) error("Clipboard copy was rejected.")
    } finally {
        body.removeChild(area)
    }
}
